import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Buildings, Users, ChartBar, Lifebuoy, Plus, Trash, Key,
  MagnifyingGlass, CheckCircle, WarningCircle, ArrowsClockwise, Copy,
  UserCircle, X, Envelope,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const SECTIONS = [
  { key: "overview", label: "Übersicht", icon: ChartBar },
  { key: "kanzleien", label: "Kanzleien", icon: Buildings },
  { key: "support", label: "Support-Inbox", icon: Lifebuoy },
];

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [active, setActive] = useState("overview");

  // Guard: nur superadmin darf rein
  useEffect(() => {
    if (user && user.role !== "superadmin") {
      toast.error("Nur für Administratoren");
      nav("/app/dashboard", { replace: true });
    }
  }, [user, nav]);

  if (!user || user.role !== "superadmin") return null;

  return (
    <div className="max-w-7xl">
      <div className="mb-8">
        <p className="overline text-[#D32F2F]">Administration</p>
        <h1 className="font-serif text-4xl mt-1">LexAuto Admin</h1>
        <p className="text-sm text-[#6B7280] mt-2">Zentrale Verwaltung aller Kanzleien, Nutzungs-KPIs und Support-Anfragen.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E5E7EB] mb-8">
        {SECTIONS.map(s => {
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              onClick={() => setActive(s.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
                active === s.key
                  ? "border-[#1A2238] text-[#1A2238]"
                  : "border-transparent text-[#6B7280] hover:text-[#0A192F]"
              }`}
              data-testid={`admin-tab-${s.key}`}
            >
              <Icon size={16} weight={active === s.key ? "fill" : "regular"} />
              {s.label}
            </button>
          );
        })}
      </div>

      {active === "overview" && <OverviewPanel />}
      {active === "kanzleien" && <KanzleienPanel />}
      {active === "support" && <SupportPanel />}
    </div>
  );
}

/* -------------- Overview / KPIs -------------- */
function OverviewPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/stats");
      setStats(r.data);
    } catch {
      toast.error("KPIs konnten nicht geladen werden");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <p className="text-sm text-[#6B7280]">Lädt …</p>;
  if (!stats) return null;

  const kpis = [
    { label: "Kanzleien gesamt", value: stats.total_kanzleien, icon: Buildings, color: "#1A2238" },
    { label: "Aktive Kanzleien", value: stats.active_kanzleien, icon: CheckCircle, color: "#16A34A" },
    { label: "Testphase läuft ab (7 Tage)", value: stats.trials_expiring_7d, icon: WarningCircle, color: "#D97706" },
    { label: "Akten insgesamt", value: stats.total_akten, icon: ChartBar, color: "#1A2238" },
    { label: "Schriftsätze", value: stats.total_schriftsaetze, icon: ChartBar, color: "#1A2238" },
    { label: "Mandanten", value: stats.total_mandanten, icon: Users, color: "#1A2238" },
    { label: "Dokumente", value: stats.total_dokumente, icon: ChartBar, color: "#1A2238" },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-overview">
      {kpis.map(k => {
        const Icon = k.icon;
        return (
          <div key={k.label} className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid={`admin-kpi-${k.label}`}>
            <div className="flex items-start justify-between mb-3">
              <p className="overline text-[#6B7280]">{k.label}</p>
              <Icon size={20} weight="duotone" style={{ color: k.color }} />
            </div>
            <p className="font-serif text-3xl" style={{ color: k.color }}>{k.value}</p>
          </div>
        );
      })}
      <div className="col-span-full flex justify-end">
        <Button variant="outline" size="sm" onClick={load} data-testid="admin-refresh-stats">
          <ArrowsClockwise size={14} className="mr-2" /> Aktualisieren
        </Button>
      </div>
    </div>
  );
}

/* -------------- Kanzleien -------------- */
function KanzleienPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);       // Kanzlei zum Editieren
  const [pwDialog, setPwDialog] = useState(null);     // { email, password }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/kanzleien");
      setItems(r.data);
    } catch {
      toast.error("Kanzleien konnten nicht geladen werden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter(k => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (k.email || "").toLowerCase().includes(s)
      || (k.kanzlei_name || "").toLowerCase().includes(s)
      || (k.full_name || "").toLowerCase().includes(s);
  });

  const resetPw = async (kid) => {
    if (!window.confirm("Neues Zufalls-Passwort für diese Kanzlei erzeugen?")) return;
    try {
      const r = await api.post(`/admin/kanzleien/${kid}/reset-password`, {});
      setPwDialog({ email: r.data.email, password: r.data.password });
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Passwort-Reset"); }
  };

  const del = async (kid, email) => {
    if (!window.confirm(`Kanzlei "${email}" UNWIDERRUFLICH löschen? Alle Akten, Dokumente und Mandanten gehen verloren.`)) return;
    try {
      await api.delete(`/admin/kanzleien/${kid}`);
      toast.success("Kanzlei gelöscht");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };

  const impersonate = async (kid, email) => {
    if (!window.confirm(`Als "${email}" anmelden? (Ihr Admin-Login geht verloren, bis Sie sich neu einloggen.)`)) return;
    try {
      const r = await api.post(`/admin/kanzleien/${kid}/impersonate`);
      // Token + User in localStorage tauschen
      localStorage.setItem("kvr_token", r.data.access_token);
      localStorage.setItem("kvr_user", JSON.stringify(r.data.user));
      toast.success(`Angemeldet als ${email}`);
      window.location.href = "/app/dashboard";
    } catch (e) { toast.error(e?.response?.data?.detail || "Impersonieren fehlgeschlagen"); }
  };

  const toggleActive = async (k) => {
    try {
      await api.patch(`/admin/kanzleien/${k.id}`, { is_active: k.is_active === false ? true : false });
      toast.success(k.is_active === false ? "Kanzlei aktiviert" : "Kanzlei gesperrt");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-kanzleien">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Suchen (E-Mail, Kanzlei, Name) …" className="pl-9" data-testid="admin-kanzlei-search" />
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="admin-kanzlei-create-btn">
          <Plus size={14} className="mr-2" /> Neue Kanzlei
        </Button>
      </div>

      {loading && <p className="text-sm text-[#6B7280]">Lädt …</p>}
      {!loading && filtered.length === 0 && <p className="text-sm text-[#6B7280]">Keine Kanzleien gefunden.</p>}

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] border-b border-[#E5E7EB]">
            <tr className="text-left">
              <th className="px-4 py-3 overline text-[#6B7280]">Kanzlei / Name</th>
              <th className="px-4 py-3 overline text-[#6B7280]">E-Mail</th>
              <th className="px-4 py-3 overline text-[#6B7280]">Status</th>
              <th className="px-4 py-3 overline text-[#6B7280]">Testphase</th>
              <th className="px-4 py-3 overline text-[#6B7280]">Letzter Login</th>
              <th className="px-4 py-3 overline text-[#6B7280] text-right">Nutzung</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(k => (
              <tr key={k.id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`admin-kanzlei-row-${k.id}`}>
                <td className="px-4 py-3">
                  <p className="font-medium text-[#0A192F]">{k.kanzlei_name}</p>
                  <p className="text-xs text-[#6B7280]">{k.full_name}</p>
                </td>
                <td className="px-4 py-3 text-[#4B5563]">{k.email}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggleActive(k)}
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      k.is_active === false
                        ? "bg-[#FEF2F2] text-[#B91C1C] hover:bg-[#FEE2E2]"
                        : "bg-[#F0FDF4] text-[#15803D] hover:bg-[#DCFCE7]"
                    }`}
                    data-testid={`admin-toggle-${k.id}`}
                  >
                    {k.is_active === false ? "gesperrt" : "aktiv"}
                  </button>
                </td>
                <td className="px-4 py-3 text-xs text-[#6B7280]">
                  {k.trial_until ? new Date(k.trial_until).toLocaleDateString("de-DE") : "—"}
                </td>
                <td className="px-4 py-3 text-xs text-[#6B7280]">
                  {k.last_login_at ? new Date(k.last_login_at).toLocaleString("de-DE") : "—"}
                </td>
                <td className="px-4 py-3 text-right text-xs text-[#6B7280]">
                  {k.stats?.akten || 0} Akten · {k.stats?.schriftsaetze || 0} Schr. · {k.stats?.mandanten || 0} Mand.
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setEditing(k)} title="Bearbeiten" className="p-1.5 hover:bg-[#F3F4F6] rounded" data-testid={`admin-edit-${k.id}`}>
                      <ArrowsClockwise size={14} className="text-[#6B7280]" />
                    </button>
                    <button onClick={() => impersonate(k.id, k.email)} title="Als Kanzlei anmelden" className="p-1.5 hover:bg-[#F3F4F6] rounded" data-testid={`admin-impersonate-${k.id}`}>
                      <UserCircle size={14} className="text-[#1A2238]" />
                    </button>
                    <button onClick={() => resetPw(k.id)} title="Passwort neu setzen" className="p-1.5 hover:bg-[#F3F4F6] rounded" data-testid={`admin-resetpw-${k.id}`}>
                      <Key size={14} className="text-[#D97706]" />
                    </button>
                    <button onClick={() => del(k.id, k.email)} title="Löschen" className="p-1.5 hover:bg-[#FEF2F2] rounded" data-testid={`admin-delete-${k.id}`}>
                      <Trash size={14} className="text-[#B91C1C]" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateKanzleiDialog onClose={() => setShowCreate(false)} onCreated={(pw) => { setShowCreate(false); setPwDialog(pw); load(); }} />}
      {editing && <EditKanzleiDialog kanzlei={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {pwDialog && <PasswordDialog data={pwDialog} onClose={() => setPwDialog(null)} />}
    </div>
  );
}

function CreateKanzleiDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "", full_name: "", kanzlei_name: "", telefon: "",
    kanzlei_adresse: "", kanzlei_plz: "", kanzlei_ort: "",
    trial_until: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.trial_until) {
        // Datum → ISO Ende des Tages
        payload.trial_until = new Date(form.trial_until + "T23:59:59Z").toISOString();
      } else {
        delete payload.trial_until;
      }
      const r = await api.post("/admin/kanzleien", payload);
      toast.success("Kanzlei angelegt");
      onCreated({ email: r.data.email, password: r.data.password });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Anlegen");
    } finally { setSaving(false); }
  };

  return (
    <Dialog onClose={onClose} title="Neue Kanzlei anlegen" testid="admin-create-dialog">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Vor- und Nachname *</Label>
            <Input value={form.full_name} onChange={e => set("full_name", e.target.value)} required data-testid="create-full-name" />
          </div>
          <div>
            <Label>E-Mail *</Label>
            <Input type="email" value={form.email} onChange={e => set("email", e.target.value)} required data-testid="create-email" />
          </div>
          <div className="sm:col-span-2">
            <Label>Kanzlei-Name *</Label>
            <Input value={form.kanzlei_name} onChange={e => set("kanzlei_name", e.target.value)} required data-testid="create-kanzlei-name" />
          </div>
          <div>
            <Label>Telefon</Label>
            <Input value={form.telefon} onChange={e => set("telefon", e.target.value)} />
          </div>
          <div>
            <Label>Testphase bis</Label>
            <Input type="date" value={form.trial_until} onChange={e => set("trial_until", e.target.value)} data-testid="create-trial" />
          </div>
          <div className="sm:col-span-2">
            <Label>Adresse</Label>
            <Input value={form.kanzlei_adresse} onChange={e => set("kanzlei_adresse", e.target.value)} placeholder="Straße & Hausnr." />
          </div>
          <div>
            <Label>PLZ</Label>
            <Input value={form.kanzlei_plz} onChange={e => set("kanzlei_plz", e.target.value)} />
          </div>
          <div>
            <Label>Ort</Label>
            <Input value={form.kanzlei_ort} onChange={e => set("kanzlei_ort", e.target.value)} />
          </div>
        </div>
        <p className="text-xs text-[#6B7280]">Ein sicheres Zufalls-Passwort wird automatisch generiert und Ihnen einmalig angezeigt.</p>
        <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E7EB]">
          <Button type="button" variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button type="submit" disabled={saving} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="create-submit">
            {saving ? "Wird angelegt …" : "Kanzlei anlegen"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function EditKanzleiDialog({ kanzlei, onClose, onSaved }) {
  const [form, setForm] = useState({
    kanzlei_name: kanzlei.kanzlei_name || "",
    full_name: kanzlei.full_name || "",
    telefon: kanzlei.telefon || "",
    kanzlei_adresse: kanzlei.kanzlei_adresse || "",
    kanzlei_plz: kanzlei.kanzlei_plz || "",
    kanzlei_ort: kanzlei.kanzlei_ort || "",
    trial_until: kanzlei.trial_until ? kanzlei.trial_until.substring(0, 10) : "",
    is_active: kanzlei.is_active !== false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.trial_until) payload.trial_until = new Date(form.trial_until + "T23:59:59Z").toISOString();
      else payload.trial_until = null;
      await api.patch(`/admin/kanzleien/${kanzlei.id}`, payload);
      toast.success("Änderungen gespeichert");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  return (
    <Dialog onClose={onClose} title={`Kanzlei bearbeiten: ${kanzlei.kanzlei_name}`} testid="admin-edit-dialog">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Vor- und Nachname</Label>
            <Input value={form.full_name} onChange={e => set("full_name", e.target.value)} />
          </div>
          <div>
            <Label>Kanzlei-Name</Label>
            <Input value={form.kanzlei_name} onChange={e => set("kanzlei_name", e.target.value)} />
          </div>
          <div>
            <Label>Telefon</Label>
            <Input value={form.telefon} onChange={e => set("telefon", e.target.value)} />
          </div>
          <div>
            <Label>Testphase bis</Label>
            <Input type="date" value={form.trial_until} onChange={e => set("trial_until", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label>Adresse</Label>
            <Input value={form.kanzlei_adresse} onChange={e => set("kanzlei_adresse", e.target.value)} />
          </div>
          <div>
            <Label>PLZ</Label>
            <Input value={form.kanzlei_plz} onChange={e => set("kanzlei_plz", e.target.value)} />
          </div>
          <div>
            <Label>Ort</Label>
            <Input value={form.kanzlei_ort} onChange={e => set("kanzlei_ort", e.target.value)} />
          </div>
          <div className="flex items-center gap-2 sm:col-span-2 pt-2">
            <input id="is_active" type="checkbox" checked={form.is_active} onChange={e => set("is_active", e.target.checked)} className="h-4 w-4" />
            <Label htmlFor="is_active" className="cursor-pointer mb-0">Kanzlei aktiv (Login erlaubt)</Label>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E7EB]">
          <Button type="button" variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button type="submit" disabled={saving} className="bg-[#1A2238] hover:bg-[#2C3859]">
            {saving ? "Speichert …" : "Speichern"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function PasswordDialog({ data, onClose }) {
  const copy = () => {
    navigator.clipboard.writeText(data.password);
    toast.success("Passwort in Zwischenablage kopiert");
  };
  return (
    <Dialog onClose={onClose} title="Zugangsdaten" testid="admin-pw-dialog">
      <div className="space-y-4">
        <div className="bg-[#FEF3C7] border border-[#F59E0B] rounded p-3 text-sm text-[#92400E]">
          Bitte notieren Sie sich das Passwort — es wird <strong>nur einmal angezeigt</strong>.
        </div>
        <div>
          <Label className="overline">E-Mail</Label>
          <p className="font-mono text-sm bg-[#F9FAFB] px-3 py-2 rounded border border-[#E5E7EB]">{data.email}</p>
        </div>
        <div>
          <Label className="overline">Passwort</Label>
          <div className="flex gap-2">
            <p className="font-mono text-sm bg-[#F9FAFB] px-3 py-2 rounded border border-[#E5E7EB] flex-1" data-testid="admin-pw-display">{data.password}</p>
            <Button variant="outline" size="sm" onClick={copy} data-testid="admin-pw-copy">
              <Copy size={14} />
            </Button>
          </div>
        </div>
        <div className="flex justify-end pt-2 border-t border-[#E5E7EB]">
          <Button onClick={onClose} className="bg-[#1A2238] hover:bg-[#2C3859]">Verstanden</Button>
        </div>
      </div>
    </Dialog>
  );
}

/* -------------- Support Inbox -------------- */
function SupportPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/support");
      setItems(r.data);
    } catch { toast.error("Support-Anfragen konnten nicht geladen werden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <p className="text-sm text-[#6B7280]">Lädt …</p>;
  if (items.length === 0) return <p className="text-sm text-[#6B7280]" data-testid="admin-support-empty">Keine offenen Support-Anfragen.</p>;

  const badge = (kat) => {
    const map = {
      frage: { bg: "#DBEAFE", fg: "#1E40AF", label: "Frage" },
      fehler: { bg: "#FEE2E2", fg: "#B91C1C", label: "Fehler" },
      feature: { bg: "#DCFCE7", fg: "#15803D", label: "Feature" },
      sonstige: { bg: "#F3F4F6", fg: "#374151", label: "Sonstige" },
    };
    const c = map[kat] || map.sonstige;
    return <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ background: c.bg, color: c.fg }}>{c.label}</span>;
  };

  return (
    <div className="space-y-3" data-testid="admin-support">
      {items.map((it, idx) => (
        <div key={idx} className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <p className="font-medium text-[#0A192F]">{it.subject || "(kein Betreff)"}</p>
              <p className="text-xs text-[#6B7280] mt-0.5">
                <Envelope size={12} className="inline mr-1" />
                {it.from_name || "—"} · {it.from_email || "—"} · <span className="text-[#1A2238]">{it.kanzlei_name || "—"}</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              {badge(it.kategorie)}
              <span className="text-xs text-[#9CA3AF]">
                {it.created_at ? new Date(it.created_at).toLocaleString("de-DE") : ""}
              </span>
            </div>
          </div>
          <p className="text-sm text-[#374151] whitespace-pre-wrap">{it.message}</p>
        </div>
      ))}
    </div>
  );
}

/* -------------- Reusable Dialog -------------- */
function Dialog({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid={testid}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
          <h3 className="font-serif text-xl">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-[#F3F4F6] rounded" aria-label="Schließen">
            <X size={18} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
